import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";
import { TableField, PageData } from "../../types";
import { rawApi, api } from "@/lib";
import Store from "../../index";
import { formatTruckTemplateWeekdays, formatTruckTemplateZip } from "../../../utils";
import {
  TruckTemplateListItem,
  GetTruckTemplateRequest,
  TruckTemplate
} from "@/models/api/truckTemplate/getTruckTemplates";
import {
  CreateTruckTemplateRequest,
  CreateTruckTemplateResponse
} from "@/models/api/truckTemplate/createTruckTemplates";
import {
  UpdateTruckTemplateResponse,
  UpdateTruckTemplateRequest
} from "@/models/api/truckTemplate/updateTruckTemplates";
import {
  DeleteTruckTemplateRequest,
  DeleteTruckTemplateResponse
} from "@/models/api/truckTemplate/deleteTruckTemplates";
import { AxiosResponse } from "axios";

const name = "truckTemplate";

if (Store.state[name]) {
  Store.unregisterModule(name);
}

@Module({ name: name, dynamic: true, store: Store, namespaced: true })
export default class TruckTemplateModule extends VuexModule {
  public currentTruckTemplate?: TruckTemplate = null;
  public data?: Array<TruckTemplateListItem> = [];
  public loading: boolean = false;
  public error?: boolean = null;
  public hideManifestedTruck: boolean = false;
  public showOnlyScheduledTrucks: boolean = false;
  public showOnlyIncorrectTruck: boolean = false;
  public errorMessage: string = null;
  public truckTemplateFields: Array<TableField> = [
    { key: "facilityCode", sortable: false, label: "Facility", hidden: false },
    { key: "selected", label: "", hidden: false },
    { key: "name", sortable: false, label: "Truck Template Name", hidden: false },
    { key: "maxCubes", sortable: false, label: "Max Cubes", hidden: false, textAlign: "right" },
    { key: "tripDays", sortable: false, label: "Trip Days", hidden: false, textAlign: "right" },
    {
      key: "truckTemplateZip3",
      sortable: false,
      label: "Zip3s",
      formatter: formatTruckTemplateZip,
      hidden: false
    },
    {
      key: "truckTemplateWeekdays",
      sortable: false,
      label: "Week Days",
      formatter: formatTruckTemplateWeekdays,
      hidden: false
    },
    {
      key: "actions",
      label: ""
    }
  ];

  public weekdays = [
    { text: "Monday", value: 2 },
    { text: "Tuesday", value: 3 },
    { text: "Wednesday", value: 4 },
    { text: "Thursday", value: 5 },
    { text: "Friday", value: 6 }
  ];

  public pageData: PageData = {
    totalPages: null,
    totalItems: null,
    currentPage: 1,
    pageSize: 30
  };

  @Action
  async getTruckTemplates(params: GetTruckTemplateRequest): Promise<void> {
    const pageSize = params ? params.pageSize : null || this.pageData.pageSize || 30;
    const pageNumber = params ? params.pageNumber : null || this.pageData.currentPage + 1;

    if (pageNumber === 1) {
      this.resetTruckTemplateList();
    }

    let target = `/trucksTemplate?page=${pageNumber}&PageSize=${pageSize}`;

    if (params.facilityFilter) {
      target += `&facilityCode=${params.facilityFilter}`;
    }

    try {
      var response = await rawApi.get<TruckTemplateListItem[]>(target);
      var { totalitems, totalpages } = response.headers;
      this.setTruckTemplateList(response);
      this.setCurrentPage(pageNumber);
      this.pageData.totalItems = totalitems;
      this.pageData.totalPages = totalpages;
    } catch (ex) {
      throw ex;
    } finally {
      this.setLoading(false);
    }
  }

  @Action
  async loadTruckTemplate(truckTemplateId: number): Promise<void> {
    this.setcurrentTruckTemplate(await this.getTruckTemplate(truckTemplateId));
  }

  @Action
  async reloadTruckTemplateTemplate(): Promise<void> {
    if (this.currentTruckTemplate)
      this.setcurrentTruckTemplate(await this.getTruckTemplate(this.currentTruckTemplate.id));
  }

  @Action
  async getTruckTemplate(truckId: number): Promise<TruckTemplate> {
    const response = await rawApi.get<TruckTemplate>(`/trucksTemplate/${truckId}`);

    return response.data;
  }

  @Action
  async createTruckTemplate(
    request: CreateTruckTemplateRequest
  ): Promise<CreateTruckTemplateResponse> {
    const response = await rawApi.post<CreateTruckTemplateResponse>(`/trucksTemplate`, request);

    return response.data;
  }

  @Action
  async deleteTruckTemplate(
    request: DeleteTruckTemplateRequest
  ): Promise<DeleteTruckTemplateResponse> {
    const response = await rawApi.delete(`/trucksTemplate/${request.truckTemplateId}`, {
      data: request
    });

    return response.data;
  }

  @Action
  async updateTruckTemplate(
    request: UpdateTruckTemplateRequest
  ): Promise<UpdateTruckTemplateResponse> {
    const response = await rawApi.put<UpdateTruckTemplateResponse>(
      `/trucksTemplate/${request.id}`,
      request
    );
    return response.data;
  }

  @Mutation
  setcurrentTruckTemplate(truck: TruckTemplate) {
    this.currentTruckTemplate = truck;
  }

  @Mutation
  setTruckTemplateList(response: AxiosResponse<TruckTemplateListItem[]>) {
    this.pageData.totalItems = response.headers.totalitems;
    this.pageData.totalPages = response.headers.totalpages;
    this.data = response.data;
  }

  @Mutation
  resetTruckTemplateList() {
    this.pageData.totalItems = 0;
    this.pageData.totalPages = 0;
    this.data = [];
  }

  @Mutation
  setLoading(isLoading: boolean) {
    this.loading = isLoading;
  }

  @Mutation
  setCurrentPage(number: number) {
    this.pageData.currentPage = number;
  }

  @Mutation
  setTruckName(name: string) {
    this.currentTruckTemplate.name = name;
  }

  @Mutation
  setTripDays(val: number) {
    this.currentTruckTemplate.tripDays = val;
  }

  @Mutation
  setMaxCubes(val: number) {
    this.currentTruckTemplate.maxCubes = val;
  }

  @Mutation
  setZips(val: string[]) {
    this.currentTruckTemplate.truckTemplateZip3 = JSON.parse(JSON.stringify(val));
  }

  @Mutation
  setWeekdays(val: string[]) {
    this.currentTruckTemplate.truckTemplateWeekdays = JSON.parse(JSON.stringify(val));
  }

  get getSelectedTruckTemplates(): Array<TruckTemplateListItem> {
    if (!this.data) return [];

    return this.data.filter(x => x.selected);
  }

  get getSelectedTruckTemplateCount(): number {
    return this.getSelectedTruckTemplates.length;
  }
}
